/**
 * @fileoverview Putting a new picture into the garden.
 *
 * Choose a source, see what you chose, say when, optionally write what it
 * shows, upload. No stage: a photo carries none (2026-10-03); a plant's stage
 * is its stage history, changed on its card (PlantStageLine). Everything harder than that — the camera, the
 * Google Photos client, EXIF, the note rules — lives in a hook of its own, so
 * what is left here is the sequence. A failed upload is said under the
 * fields, with the dialog left open and everything in it kept.
 *
 * Several chosen at once (the device's chooser or Google Photos) skip the
 * preview: each is shrunk, dated from its own EXIF (or Google's date, or when
 * the file was written) and sent on its own, a few at a time, as the Photos
 * page does. A dropped connection loses one picture, not the batch, and the
 * count says how far it has got. They go without a note; one can be added on
 * each afterwards. One chosen keeps the full screen: preview, date, note.
 *
 * Where: a plant's or a bed's card, "Add a photo" (through
 * PhotoUploadDialog), and ShareMoments.
 * API: POST /photos/add, or POST /locations/:id/photos (uploadPhotoTo);
 * the words first, through usePhotoNote (POST /notes/:plantObjId or
 * /locations/:id/journal).
 * Settings: TRAY_UPLOAD_CONCURRENCY (config/tunables/garden) for several at
 * once; VITE_GOOGLE_CLIENT_ID.
 * Words (en/es): plants.photo.* (addTitle, addAction, addingCount,
 * chooseAnother, chosenAlt, someFailed, uploadFailed), sitewide.close, sitewide.cancel, sitewide.saving (Button loading),
 * photoCopies.* (skippedOne, skipped, undo).
 * Watch out: a plant's first photo is noted for ShareMoments
 * (noteFirstPhoto), so the "first photo" moment can offer itself.
 * An exact copy of a photo already added is skipped by the server (B61): the dialog says "Already added, 1 copy
 * skipped" with Undo (lib/photoCopies), counts it as neither added nor failed, and closes as if it went in; Undo adds
 * it after all. A Google Photos pick sends Google's item id, so the same item picked twice is skipped.
 * Failures on our side (D31, lib/serverFailure): an upload's is said under
 * the fields with the standard sentence and the support address as a link;
 * in a batch, the count is followed by the standard sentence when any
 * picture failed that way, reported to Sentry.
 *
 * @module UploadPhotoDialog
 */

import { noteFirstPhoto } from '@/features/share/lib/shareMoments';
import React, { useContext } from 'react';
import { ImagePlus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import Spinner from '@/shared/components/ui/Spinner';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { uploadPhotoTo } from '@/shared/api/photos';
import { plantSubject, type Subject } from '@/shared/api/subject';
import { PlantPhoto } from '@/shared/types/photo';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { debugLog } from '@/config/config';
import { errorMessage } from '@/shared/lib/errorMessage';
import { isServerFailure } from '@/shared/lib/serverFailure';
import { PhotoCopySkipped } from '@/shared/lib/photoCopiesCore';
import { sayCopiesSkipped } from '@/shared/lib/photoCopies';
import { FailureText, ServerFailureNote } from '@/shared/components/ServerFailure';
import { PlantsContext } from '@/shared/context/PlantsContextDefinition';
import { PhotoNoteField } from '../PhotoNoteField';
import { PhotoDialogShell, PhotoPicture } from './PhotoDialogShell';
import { PhotoButton } from '@/shared/components/ui/PhotoButton';
import { PhotoSourcePicker, type ChosenPicture } from './PhotoSourcePicker';
import { DateTakenField } from './DateTakenField';
import { useCamera } from './useCamera';
import { useGooglePhotos } from './useGooglePhotos';
import { usePhotoChoice } from './usePhotoChoice';
import { usePhotoNote } from './usePhotoNote';
import { preparePhoto } from '@/shared/lib/preparePhoto';
import { isHeic } from '@/shared/lib/uploadablePhoto';
import { getCurrentDate } from '@/shared/utils/dateUtils';
import { TRAY_UPLOAD_CONCURRENCY as UPLOAD_CONCURRENCY } from '@/config/tunables/garden';

/** How far a batch of several photos has got, and how many of it failed. */
interface BatchProgress {
  /** Sent or failed so far. */
  done: number;
  /** In the batch. */
  total: number;
  /** Not sent. */
  failed: number;
  /** The first picture's failure that was on our side (D31), said with the standard sentence once the batch ends. */
  serverFailure?: unknown;
}

/** Whose photo this will be, and how the card hears it arrived. */
interface UploadPhotoDialogProps {
  /** Whether the dialog is showing; closing it forgets everything chosen and typed. */
  isOpen: boolean;
  /** Closes it. */
  onClose: () => void;
  /** The plant the photo goes on, unless subject names a location instead. */
  plantObjId: number;
  /** A location's photo instead of a plant's: no stage, no plant bookkeeping. */
  subject?: Subject;
  /** Told of each photo as it is stored, so the card shows it at once. */
  onPhotoUploaded: (photo: PlantPhoto) => void;
}

/**
 * The add-a-photo screen: source, preview, date, words, upload.
 *
 * @param props - UploadPhotoDialogProps.
 */
export const UploadPhotoDialog: React.FC<UploadPhotoDialogProps> = ({
  isOpen, onClose, plantObjId, subject: subjectProp, onPhotoUploaded,
}) => {
  /** The plant or bed the photo goes on; one object while the props stay the same. */
  const subject = React.useMemo(() => subjectProp ?? plantSubject(plantObjId), [subjectProp, plantObjId]);
  const isPlant = subject.kind === 'plant';
  const { reactTranslate: t } = useTranslation();
  const camera = useCamera();
  // Kept with a Google Photos pick, so its photos reach the tray, on this bed, should the dialog be gone by then.
  const google = useGooglePhotos(isOpen, subject.kind === 'plant'
    ? { kind: 'plant', id: subject.id }
    : { kind: 'bed', id: subject.id, locationId: subject.id });
  const choice = usePhotoChoice();
  const note = usePhotoNote(subject, undefined, isOpen);
  const plantsContext = useContext(PlantsContext);

  const [isUploading, setIsUploading] = React.useState(false);
  const [batch, setBatch] = React.useState<BatchProgress | null>(null);

  /*
   * Everything typed and chosen belongs to one visit to this dialog.
   *
   * Depends on the three reset functions rather than the hooks that own them:
   * each hook returns a fresh object every render, so depending on the objects
   * would run this on every render — and clearing the date to a new Date()
   * each time makes that a loop that never settles. The functions themselves
   * are stable.
   */
  const { stop: stopCamera } = camera;
  const { clear: clearChoice } = choice;
  const { reset: resetNote } = note;
  React.useEffect(() => {
    if (isOpen) return;
    stopCamera();
    clearChoice();
    resetNote();
    setIsUploading(false);
    setBatch(null);
  }, [isOpen, stopCamera, clearChoice, resetNote]);

  /**
   * Shrinks one of several pictures (converting an iPhone HEIC first), dates
   * it and sends it.
   *
   * @param picture - The file, and the day it was taken where its source says.
   * @returns The photo as stored.
   * @throws When it is a HEIC that could not be converted, or the upload fails.
   */
  const sendOne = async ({ file, takenAt, googleItemId }: ChosenPicture): Promise<PlantPhoto> => {
    let day: Date | null = takenAt ?? null;
    const prepared = await preparePhoto(file);
    // A picture that could not be made ready is not sent (the one rule, preparePhoto): counted as failed, and said.
    if (!prepared.ok) throw new Error(prepared.message);
    const toSend = prepared.file;
    day = day ?? prepared.exif.dateTaken?.toJSDate() ?? null;
    // No EXIF: when the file was last written, which off a camera is when it was taken.
    if (!day && file.lastModified) day = new Date(file.lastModified);
    const uploaded = await uploadPhotoTo(subject, toSend, day ?? getCurrentDate().startOf('day').toJSDate(), null, undefined, { googleItemId });
    onPhotoUploaded(uploaded);
    return uploaded;
  };

  /**
   * Says that exact copies were skipped, with Undo; Undo adds them after all, shown on the card at once.
   *
   * @param skipped - The copies the server skipped.
   */
  const sayCopies = (skipped: PhotoCopySkipped<PlantPhoto>[]): void => {
    sayCopiesSkipped(skipped, (stored) => {
      stored.forEach(onPhotoUploaded);
      if (isPlant) void plantsContext?.updateSinglePlant?.(String(plantObjId)).catch(() => {});
    });
  };

  /**
   * Sends several pictures, a few at a time (UPLOAD_CONCURRENCY), counting as
   * it goes. Closes when all went in; otherwise stays open saying how many failed.
   *
   * @param chosen - The pictures chosen, from the device or Google Photos.
   */
  const handleMany = async (chosen: ChosenPicture[]): Promise<void> => {
    // An iPhone photo picked on a computer often has no type, so a HEIC is known by its name or bytes.
    const isPicture = await Promise.all(chosen.map(({ file }) => (file.type.startsWith('image/') ? true : isHeic(file))));
    const pictures = chosen.filter((_, index) => isPicture[index]);
    if (pictures.length === 0) return;
    if (pictures.length === 1) {
      void choice.accept(pictures[0].file, { takenAt: pictures[0].takenAt, googleItemId: pictures[0].googleItemId });
      return;
    }

    const hadPhotos = isPlant && (plantsContext?.plants?.find((p) => p.plant_obj_id === plantObjId)?.photos?.length ?? 0) > 0;
    setBatch({ done: 0, total: pictures.length, failed: 0 });
    let failed = 0;
    const skipped: PhotoCopySkipped<PlantPhoto>[] = [];
    const queue = [...pictures];
    /** Takes pictures off the shared queue one after another until it is empty. */
    const worker = async (): Promise<void> => {
      for (let picture = queue.shift(); picture; picture = queue.shift()) {
        try {
          await sendOne(picture);
          setBatch((b) => b && { ...b, done: b.done + 1 });
        } catch (error) {
          // An exact copy of a photo already added: not stored, not a failure.
          if (error instanceof PhotoCopySkipped) {
            skipped.push(error);
            setBatch((b) => b && { ...b, done: b.done + 1 });
            continue;
          }
          debugLog('PhotoUploadDialog', 'Upload failed', error);
          failed += 1;
          setBatch((b) => b && {
            ...b,
            done: b.done + 1,
            failed: b.failed + 1,
            serverFailure: b.serverFailure ?? (isServerFailure(error) ? error : undefined),
          });
        }
      }
    };
    await Promise.all(Array.from({ length: UPLOAD_CONCURRENCY }, worker));

    if (isPlant) {
      if (!hadPhotos && failed < pictures.length) noteFirstPhoto(plantObjId);
      void plantsContext?.updateSinglePlant?.(String(plantObjId)).catch(() => {});
    }
    sayCopies(skipped);
    if (failed === 0) onClose();
  };

  /**
   * Saves the words, uploads the picture, and brings the plant in step.
   *
   * Either failing is said under the fields, in the server's words where it
   * explained a refusal (too large, not a picture). The words failing used to
   * say "The photo was saved, but its note could not be", when it stops the
   * upload before it starts; an upload failing said nothing at all.
   */
  const handleUpload = async (): Promise<void> => {
    if (!choice.file) return;

    setIsUploading(true);
    note.setError(null);
    try {
      let noteId: number | null;
      try {
        noteId = await note.saveForNew();
      } catch (noteFailure) {
        debugLog('PhotoUploadDialog', 'Could not save the photo note:', noteFailure);
        note.setError(errorMessage(noteFailure, t('plants.photo.uploadFailed')));
        return;
      }

      // Read before the upload: whether this is the plant's first photograph.
      const hadPhotos = isPlant && (plantsContext?.plants?.find((p) => p.plant_obj_id === plantObjId)?.photos?.length ?? 0) > 0;
      const uploaded = await uploadPhotoTo(subject, choice.file, choice.dateTaken, noteId, undefined, { googleItemId: choice.googleItemId });

      // A photo arriving with words already on it: the plants payload has
      // never heard of that note.
      await note.sync();

      onPhotoUploaded(uploaded);
      onClose();
      if (isPlant && !hadPhotos) noteFirstPhoto(plantObjId);
      // The card shows the new picture at once; the rest of the plant — its
      // stage and dates, which the server moves with a new photograph — comes
      // in behind it, for the card, the tile and the row alike.
      if (isPlant) void plantsContext?.updateSinglePlant?.(String(plantObjId)).catch(() => {});
    } catch (error) {
      // An exact copy of a photo already added: said with Undo, and the dialog closes as if it went in.
      if (error instanceof PhotoCopySkipped) {
        sayCopies([error]);
        onClose();
        return;
      }
      debugLog('PhotoUploadDialog', 'Upload error:', error);
      note.setError(errorMessage(error, t('plants.photo.uploadFailed')));
    } finally {
      setIsUploading(false);
    }
  };

  /** Uploading, or still shrinking the chosen picture: either way, hands off. */
  const busy = isUploading || choice.isProcessing;

  // Pinned at the foot once there is a picture to add.
  const footer = choice.previewUrl ? (
    <ActionRow onCancel={onClose} disabled={busy}>
      <Button size="lg" onClick={handleUpload} loading={isUploading} disabled={busy}>
        <ImagePlus className="mr-2 h-4 w-4" aria-hidden="true" />
        {t('plants.photo.addAction')}
      </Button>
    </ActionRow>
  ) : undefined;

  const batchRunning = batch !== null && batch.done < batch.total;

  return (
    <PhotoDialogShell isOpen={isOpen} onClose={onClose} title={t('plants.photo.addTitle')} footer={footer}>
      {batch ? (
        <div className="shrink-0 space-y-3 py-4 text-center" aria-live="polite">
          {batchRunning ? (
            <p className="flex items-center justify-center gap-2 text-sm">
              <Spinner size={18} />
              {t('plants.photo.addingCount', { done: batch.done, total: batch.total })}
            </p>
          ) : (
            <>
              <p className="text-sm text-destructive">
                {t('plants.photo.someFailed', { count: batch.failed, total: batch.total })}
              </p>
              {batch.serverFailure !== undefined && (
                <ServerFailureNote error={batch.serverFailure} where="uploadPhoto.batch" className="text-sm text-destructive" />
              )}
              <Button variant="outline" size="lg" onClick={onClose}>{t('sitewide.close')}</Button>
            </>
          )}
        </div>
      ) : choice.previewUrl ? (
        <>
          {/* The picture takes what room the fields leave it, and gives way
              first, so nothing here scrolls. */}
          <PhotoPicture src={choice.previewUrl} alt={t('plants.photo.chosenAlt')} busy={busy}>
            <div className="absolute right-2 top-2">
              <PhotoButton size="pill" label={t('plants.photo.chooseAnother')} onClick={choice.clear} disabled={busy}>
                {t('plants.photo.chooseAnother')}
              </PhotoButton>
            </div>
          </PhotoPicture>

          <div className="grid shrink-0 grid-cols-1 gap-2">
            <DateTakenField
              value={choice.dateTaken}
              onChange={choice.setDate}
              source={choice.dateSource}
              disabled={busy}
            />
          </div>

          {/* Words for the photo. A picture of blossom end rot has nowhere to
              say so, and typing it here is the one moment the gardener is
              certainly looking at the thing. */}
          <PhotoNoteField value={note.value} onChange={note.setValue} disabled={busy} />

          {note.error && (
            <p className="shrink-0 text-sm text-destructive"><FailureText text={note.error} /></p>
          )}
        </>
      ) : (
        <>
          <PhotoSourcePicker
            camera={camera}
            onCaptured={(file) => void choice.accept(file, { trustExif: false })}
            onFileChosen={(file, takenAt, googleItemId) => void choice.accept(file, { takenAt, googleItemId })}
            onFilesChosen={(pictures) => void handleMany(pictures)}
            onPickGoogle={google.pick}
            isPickingGoogle={google.isPicking}
            googleStatus={google.status}
            googleCount={google.count}
            onCancelGoogle={google.cancel}
            googleError={google.error}
          />
          {/* A picture that was chosen and let go (an iPhone photo that would
              not convert), said where the next choice is made. */}
          {choice.error && (
            <p role="alert" className="shrink-0 text-sm text-destructive">{choice.error}</p>
          )}
        </>
      )}
    </PhotoDialogShell>
  );
};
